/**
 * PriInput — Chat input textarea with Enter-to-send, Shift+Enter for newline.
 * Max 2000 chars, disabled during loading. Send button with arrow icon.
 */
import { usePriChat } from '@/hooks/usePriChat';
import { usePriStore } from '@/stores/pri-store';
import { useTranslation } from '@kgc/i18n';
import { cn } from '@kgc/ui';
import { useCallback, useRef, useState } from 'react';

/** Maximum input characters */
const MAX_CHARS = 2000;

export function PriInput() {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isLoading = usePriStore(s => s.isLoading);
  const { sendMessage: sendToApi } = usePriChat();

  const sendMessage = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isLoading) return;

    // sendToApi adds the user message to the store AND calls the backend API
    void sendToApi(trimmed);
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, isLoading, sendToApi]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const newValue = e.target.value.slice(0, MAX_CHARS);
    setValue(newValue);

    // Auto-grow textarea
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }

  const canSend = value.trim().length > 0 && !isLoading;

  return (
    <div className="border-t border-slate-700 px-3 py-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          placeholder={t('pri.inputPlaceholder')}
          rows={1}
          className={cn(
            'max-h-[120px] min-h-[40px] flex-1 resize-none rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 text-sm text-white outline-none transition-colors',
            'placeholder:text-slate-500',
            'focus:border-blue-500',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        />
        <button
          onClick={sendMessage}
          disabled={!canSend}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
            canSend
              ? 'bg-blue-600 text-white hover:bg-blue-500'
              : 'bg-slate-700 text-slate-500 cursor-not-allowed'
          )}
          aria-label={t('pri.sendLabel')}
        >
          {/* Arrow-up send icon (SVG) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5"
          >
            <path
              fillRule="evenodd"
              d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      {value.length > MAX_CHARS * 0.9 && (
        <p className="mt-1 text-right text-[10px] text-slate-500">
          {value.length}/{MAX_CHARS}
        </p>
      )}
    </div>
  );
}
